import { PenaltyReconfirmation } from './PenaltyReconfirmation';
import { CancellationPenaltyLabel } from './CancellationPenaltyLabel';
import { HotelReservationDialog } from '@frontend/features/hotels/HotelReservationDialog';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HoldRetentionNotice } from './HoldRetentionNotice';
import { useHoldCountdown } from '../holdCountdown';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import {
  apiErrorMessage,
  TravelBooking,
  useBookingTickets,
  useConnectorCapabilities,
  useConfirmBooking,
  useSyncBooking,
  useTravelBookingRefresh,
} from '../queries';
import { useAudience } from '../audience';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import {
  bookingStateColor,
  bookingStateLabelKey,
  canCancelBooking,
  canConfirmBooking,
  isOnRequest,
} from '../bookingState';
import { formatMoney } from '../money';
import { BookingExtras } from './BookingExtras';
import { BookingTickets } from './BookingTickets';
import { VoucherAction } from './VoucherAction';
import { CancelBookingDialog } from './CancelBookingDialog';
import { CancellationPolicyPanel } from './CancellationPolicyPanel';
import { useBookingCancellation } from '../bookingCancellation';
import { resolveCancellationPolicy } from '../cancellationPolicy';
import { ActiveOperationAlert, ActiveOperationChip } from './ActiveOperationStatus';
import { ConfirmPassengerContractWarning } from './ConfirmPassengerContractWarning';
import { useActiveOperationRecheck } from '../activeOperationRecheck';
import { OperationalServiceMoments } from './OperationalServiceMoments';
import { SelectedBookingOptions } from './SelectedBookingOptions';
import { ConfirmedOptionalItems } from './ConfirmedOptionalItems';
import { confirmedOptionalItemsFallbackVisible } from '../confirmedOptionalItems';
import { BookingLegsSection } from './BookingLegsSection';
import { CarBookingPaymentBreakdown } from './CarBookingPaymentBreakdown';
import {
  connectorCapabilityAuthorityIsActionable,
  connectorCapabilityVerdict,
  resolveConnectorCapabilities,
} from '../connectorCapabilityResolution';
import {
  bookingRecoverySurface,
  sameAttemptRetryAvailability,
} from '../bookingRecovery';
import { BookingRecoveryCard } from './BookingRecoveryCard';
import { ManualReconciliationPanel } from './ManualReconciliationPanel';
import { effectiveTicketResult, ticketFetchSurfaces } from '../ticket-utils';
import {
  bookingPendingQuestions,
  clearQuestionAnswersDraft,
  mandatoryAnswersExceedConfirmLimit,
  questionAnswerPayload,
  readQuestionAnswersDraft,
  saveQuestionAnswersDraft,
  unansweredMandatoryQuestions,
} from '../pendingQuestions';
import type { QuestionAnswersDraft } from '../pendingQuestions';
import { PendingQuestionsSection } from './PendingQuestionsSection';
import { bookingMomentOf } from '../bookingMoment';
import { Link } from '@frontend/features/common/components/Link';
import { routes } from '@frontend/router/routes';
import { addLeadingZeros } from '@frontend/features/common/utils/helpers';
import { defaultLeadingZeros } from '@frontend/features/common/constants';
import { useBookingTransitionFocus } from '../hooks/useBookingTransitionFocus';

interface Props {
  booking: TravelBooking | null;
  /**
   * Abre la resolución manual junto con el detalle. Lo usa la fila del listado
   * cuya acción ES "Resolver": llevar al asesor al modal y hacerle buscar el
   * botón de la tarjeta sería prometerle una acción y darle una pantalla.
   * Solo tiene efecto donde esa tarjeta existe (recuperación manual).
   */
  openManualReconciliation?: boolean;
  onClose: () => void;
}

/** El id ancla el botón de confirmar con su motivo para lectores de pantalla. */
const CONFIRM_BLOCKED_ID = 'booking-detail-confirm-blocked';

const STATE_COLOR: Record<string, 'default' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  quoted: 'info',
  held: 'info',
  confirmed: 'success',
  cancelled: 'error',
  failed: 'error',
};

function InfoItem({ label, value }: { label: string; value?: string }) {
  return (
    <Grid item xs={4}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2">{value || '—'}</Typography>
    </Grid>
  );
}

/**
 * Detalle operativo de una reserva desde el listado: estado, vencimiento del
 * hold, quién la reservó, breakdown de precio, pasajeros, política de
 * cancelación y TODAS las acciones (confirmar/cancelar/sincronizar/tickets).
 */
export function BookingDetailModal(props: Props) {
  if (props.booking?.source === 'hotelrs') {
    return <HotelReservationDialog key={props.booking.id} reservationId={props.booking.id} onClose={props.onClose} />;
  }
  return <ProviderBookingDetailModal {...props} />;
}

function ProviderBookingDetailModal({
  booking: initial,
  openManualReconciliation = false,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const { isAgent } = useAudience();
  // Quién está logueado: el draft de respuestas es SUYO, no de la pestaña
  // (el logout no recarga la app — ver `pendingQuestions.ts`).
  const { myProfile, ability } = useAuth();
  const { addToast } = useSnackBar();
  const confirm = useConfirmBooking();
  const sync = useSyncBooking();
  const refreshBooking = useTravelBookingRefresh();
  const {
    data: connectors,
    isFetching: areConnectorCapabilitiesFetching,
    isSuccess: areConnectorCapabilitiesSuccessful,
  } = useConnectorCapabilities();
  // Estado local: las acciones devuelven la fila actualizada.
  const [booking, setBooking] = useState<TravelBooking | null>(initial);
  useHoldCountdown(booking?.state === 'held' ? booking.expiresAt : undefined);
  // `initial` cambia un render antes que el efecto que reemplaza el estado
  // local; usar su identidad oculta inmediatamente documentos de la anterior.
  const ticketsQuery = useBookingTickets(
    initial?.id ?? booking?.id,
    initial?.id,
  );
  const { headingRef, contentRef } = useBookingTransitionFocus(
    booking?.id ?? 'booking-detail',
    booking?.state ?? 'review',
  );
  /** La resolución manual, abierta desde el CTA de su tarjeta. */
  const [resolvingManual, setResolvingManual] = useState(false);
  /**
   * Lo tipeado para las preguntas del operador. Las preguntas vienen de la
   * fila; esto es el draft local de ESTA reserva (`pendingQuestions.ts`).
   */
  const [answers, setAnswers] = useState<QuestionAnswersDraft>({});
  const [resolvingManualBusy, setResolvingManualBusy] = useState(false);
  const applyAuthoritativeBooking = useCallback(
    (updated: TravelBooking) =>
      setBooking((prev) => (prev ? { ...prev, ...updated } : updated)),
    [],
  );

  // Qué sabe hacer el proveedor de ESTA reserva: las acciones que no soporta
  // se OCULTAN en vez de fallar al click (tickets, política, extras).
  const capabilityResolution = resolveConnectorCapabilities(connectors, {
    provider: booking?.provider,
    vertical: booking?.vertical ?? '',
  });
  const areConnectorCapabilitiesActionable =
    connectorCapabilityAuthorityIsActionable({
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    });
  /**
   * La política se consulta solo con disponibilidad explícita. Un snapshot
   * ausente o refrescando no autoriza otra llamada al proveedor.
   */
  const cancellationPolicyVerdict = connectorCapabilityVerdict(
    'cancellationPolicy',
    {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    },
  );
  const confirmCapabilityAvailable =
    connectorCapabilityVerdict('confirm', {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    }) === 'supported';
  const cancelCapabilityAvailable =
    connectorCapabilityVerdict('cancel', {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    }) === 'supported';
  const syncCapabilityAvailable =
    connectorCapabilityVerdict('sync', {
      resolution: capabilityResolution,
      isSuccess: areConnectorCapabilitiesSuccessful,
      isFetching: areConnectorCapabilitiesFetching,
    }) === 'supported';
  /**
   * El momento de cancelar, con la plata delante: el MISMO que el modal del
   * flujo (`bookingCancellation.ts`).
   */
  const cancellation = useBookingCancellation({
    booking,
    enquire: cancellationPolicyVerdict === 'supported',
    onBooking: applyAuthoritativeBooking,
  });
  const { reset: resetCancellation } = cancellation;

  useActiveOperationRecheck(booking, applyAuthoritativeBooking);

  useEffect(() => {
    setBooking(initial);
    setAnswers(readQuestionAnswersDraft(myProfile?.id, initial?.id));
    resetCancellation();
    // La resolución manual es del asesor, igual que su CTA: entrar por el
    // listado no puede abrirla para una audiencia que no la opera.
    setResolvingManual(openManualReconciliation && isAgent);
    setResolvingManualBusy(false);
    // El draft pertenece al usuario logueado: si la identidad cambia sin
    // recargar la app, lo que había deja de estar disponible.
  }, [initial, isAgent, myProfile?.id, openManualReconciliation, resetCancellation]);

  if (!booking) return null;

  /**
   * Tickets es la única acción cuyo rechazo del hub es un 400 pelado
   * ("el proveedor X no expone tickets via API"), sin nada accionable: se pide
   * SOLO con un sí explícito, y el "no" se le explica al agente. El veredicto
   * gobierna las DOS entradas al pedido —el botón de acciones y el refresh de
   * URLs vencidas dentro de `BookingTickets`— vía `ticketFetchSurfaces`.
   */
  const ticketsVerdict = connectorCapabilityVerdict('tickets', {
    resolution: capabilityResolution,
    isSuccess: areConnectorCapabilitiesSuccessful,
    isFetching: areConnectorCapabilitiesFetching,
  });
  const ticketSurfaces = ticketFetchSurfaces({
    verdict: ticketsVerdict,
    effective: effectiveTicketResult({
      ready: booking.ticketsReady,
      tickets: booking.tickets,
      expiresAt: booking.ticketsExpiresAt,
    }),
  });
  /**
   * Los extras se compran sobre la reserva ya creada, así que la sección se
   * ofrece con el mismo criterio que en el modal del flujo: el veredicto del
   * conector y el momento de la reserva (`extrasSectionVisible`, adentro del
   * componente). Por dónde entró el asesor no puede cambiar qué se le ofrece.
   */
  const extrasVerdict = connectorCapabilityVerdict('extras', {
    resolution: capabilityResolution,
    isSuccess: areConnectorCapabilitiesSuccessful,
    isFetching: areConnectorCapabilitiesFetching,
  });

  /**
   * Las preguntas del operador de esta reserva y las obligatorias que siguen
   * sin responder: UNA lista, de la que salen la sección que se dibuja, el
   * bloqueo del botón de confirmar y lo que viaja en el confirm.
   */
  const pendingQuestions = bookingPendingQuestions(booking);
  const unansweredQuestions = unansweredMandatoryQuestions(
    pendingQuestions,
    answers,
  );
  const mandatoryAnswersOverflow =
    mandatoryAnswersExceedConfirmLimit(pendingQuestions);
  const confirmBlockedByQuestions =
    unansweredQuestions.length > 0 || mandatoryAnswersOverflow;
  const answerQuestion = (key: string, value: string) =>
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      saveQuestionAnswersDraft(myProfile?.id, booking.id, next);
      return next;
    });

  const money = (amountMinor?: number, currency = booking.currency) =>
    amountMinor != null && currency
      ? formatMoney({ amountMinor, currency })
      : '—';

  const run = async (
    action: () => Promise<TravelBooking>,
    errorKey: string,
    bookingId: string,
  ) => {
    try {
      const updated = await action();
      setBooking((prev) => ({ ...prev, ...updated }));
    } catch (error) {
      addToast({ message: apiErrorMessage(error) ?? t(errorKey), color: 'error' });
      await refreshBooking(bookingId, applyAuthoritativeBooking);
    }
  };

  /**
   * La política, resuelta UNA vez para las dos superficies que la muestran:
   * el panel de "ver política" y el diálogo que cancela.
   */
  const policyView = resolveCancellationPolicy({
    policy: cancellation.policy,
    isLoading: cancellation.isLoadingPolicy,
  });

  const serviceName =
    booking.fareSnapshot?.name ?? booking.fareSnapshot?.title ?? '';
  const canOperate = Boolean(booking.ruteroBookingId);
  /**
   * La MISMA tarjeta que el modal del flujo: el estado raro no puede leerse
   * distinto según por dónde entró el asesor.
   */
  const recoverySurface = bookingRecoverySurface(booking);
  const busy =
    confirm.isLoading ||
    cancellation.isCancelling ||
    sync.isLoading ||
    resolvingManualBusy;

  const syncBooking = () =>
    run(
      () => sync.mutateAsync(booking.id),
      'travelServices.booking.syncError',
      booking.id,
    );
  /**
   * Desde el listado no hay envío que repetir: el reenvío del mismo intento
   * vive en la pantalla de reserva, con el holder del intento. La tarjeta lo
   * dice y nombra el camino que queda en vez de prometer un botón.
   */
  const retryAvailability = sameAttemptRetryAvailability({ canResendHold: false });
  const retryBlockedNote = retryAvailability.enabled
    ? undefined
    : t(retryAvailability.noteKey);

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle
        component="div"
        ref={headingRef}
        tabIndex={-1}
        aria-live="polite"
        sx={{ '&:focus': { outline: 'none' } }}
      >
        {t('travelServices.detail.title')}
        {serviceName ? ` — ${serviceName}` : ''}
      </DialogTitle>
      <DialogContent ref={contentRef}>
        <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Stack direction="row" spacing={1} alignItems="center">
              <Tooltip
                title={
                  booking.state === 'held'
                    ? t('travelServices.booking.holdTooltip')
                    : ''
                }
              >
                <Chip
                  sx={{ cursor: booking.state === 'held' ? 'help' : 'default' }}
                  color={bookingStateColor(
                    booking.state,
                    booking.providerStatus,
                    STATE_COLOR,
                  )}
                  label={t(
                    bookingStateLabelKey(booking.state, booking.providerStatus),
                  )}
                />
              </Tooltip>
              <ActiveOperationChip booking={booking} />
              {booking.providerStatus && !isOnRequest(booking.providerStatus) && (
                <Chip variant="outlined" size="small" label={booking.providerStatus} />
              )}
            </Stack>
            <Typography variant="h6">{money(booking.finalAmountMinor)}</Typography>
          </Box>

          {/* On-request: la reserva existe pero el operador todavia puede
              rechazarla. Se dice explicito, no solo con el color del chip. */}
          {isOnRequest(booking.providerStatus) && (
            <Alert severity="warning">{t('travelServices.detail.onRequestNotice')}</Alert>
          )}

          <HoldRetentionNotice booking={booking} />
          <ActiveOperationAlert booking={booking} />
          <ConfirmPassengerContractWarning booking={booking} />

          {/* El estado raro con SU cara y SU acción. El reintento del hold no
              se ofrece desde el listado: acá no hay envío que repetir. */}
          {recoverySurface && (
            <BookingRecoveryCard
              spec={recoverySurface.spec}
              technicalCode={recoverySurface.technicalCode}
              reference={booking.providerReference}
              bookingId={booking.ruteroBookingId ?? booking.id}
              loading={recoverySurface.spec.action === 'sync' && sync.isLoading}
              onAction={
                recoverySurface.spec.action === 'sync' && syncCapabilityAvailable
                  ? syncBooking
                  : recoverySurface.spec.action === 'manual' && isAgent
                    ? () => setResolvingManual(true)
                    : undefined
              }
              disabledNote={
                recoverySurface.spec.action === 'retry'
                  ? retryBlockedNote
                  : undefined
              }
            />
          )}

          {recoverySurface?.spec.action === 'manual' && resolvingManual && (
            <ManualReconciliationPanel
              booking={booking}
              onResolved={applyAuthoritativeBooking}
              onBusyChange={setResolvingManualBusy}
            />
          )}

          {booking.state === 'held' && booking.expiresAt && (
            <Alert severity="warning">
              {t('travelServices.detail.holdExpires', {
                date: new Date(booking.expiresAt).toLocaleString(),
              })}
            </Alert>
          )}

          <Grid container spacing={1.5}>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" display="block">
                {t('travelServices.detail.case')}
              </Typography>
              {booking.case ? (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2">
                    {t('travelServices.bookingList.case.number', {
                      number: addLeadingZeros(
                        booking.case.id,
                        defaultLeadingZeros,
                      ),
                    })}{' '}
                    · {booking.case.name} · {booking.case.client.firstName}{' '}
                    {booking.case.client.lastName}
                  </Typography>
                  {ability?.can('manage', 'Cases') && (
                    <Link
                      to={routes.dashboard.caseView.replace(
                        ':id',
                        String(booking.case.id),
                      )}
                    >
                      {t('travelServices.detail.openCase')}
                    </Link>
                  )}
                </Stack>
              ) : (
                <Typography variant="body2">
                  {t('travelServices.bookingList.case.none')}
                </Typography>
              )}
            </Grid>
            <InfoItem
              label={t('rutero.connectors.provider')}
              value={booking.provider}
            />
            <InfoItem
              label={t('rutero.connectors.vertical')}
              value={t(`rutero.verticals.${booking.vertical}`)}
            />
            <InfoItem label="Ref" value={booking.providerReference} />
            <InfoItem
              label={t('travelServices.detail.createdBy')}
              value={booking.createdByEmail ?? booking.createdBy}
            />
            <InfoItem
              label={t('travelServices.createdAt')}
              value={new Date(booking.createdAt).toLocaleString()}
            />
            <InfoItem
              label={t('travelServices.detail.updatedAt')}
              value={new Date(booking.updatedAt).toLocaleString()}
            />
          </Grid>

          <Divider />

          {/* El recorrido, ya sin búsqueda viva: sale de los puntos que
              quedaron guardados en la reserva al holdear, que son los mismos
              que se dibujaron entonces — con su rótulo, así que la nota sigue
              diciendo si el recorrido lo confirmó el proveedor o se pidió. */}
          <CancellationPenaltyLabel booking={booking} />
          <PenaltyReconfirmation booking={booking} />
          <OperationalServiceMoments booking={booking} />

          <SelectedBookingOptions booking={booking} />

          {/* Los tramos del traslado (recogida con su estado, punto de
              encuentro, vehículo), desde el registro autoritativo de la
              reserva (§21.23). Se autooculta sin `legs`: hoy solo los publica
              el vertical transfers. */}
          <BookingLegsSection
            legs={booking.legs}
            passengers={booking.passengers}
            bookingState={booking.state}
          />

          {/* Lo que el operador pregunta, con la MISMA cara que en el flujo de
              reserva: por dónde entró el asesor no puede cambiar lo que hay
              que responder. Se dibuja bajo la MISMA condición que ofrece el
              botón de confirmar: sin ese botón no hay nada que estas
              respuestas puedan destrabar. */}
          {canOperate && confirmCapabilityAvailable && canConfirmBooking(booking) && (
            <PendingQuestionsSection
              questions={pendingQuestions}
              answers={answers}
              onAnswer={answerQuestion}
              passengers={booking.passengers}
            />
          )}

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('travelServices.detail.pricingSection')}
            </Typography>
            <Grid container spacing={1.5}>
              {/* El desglose (neto, markup, comisión) es del asesor. Para una
                  audiencia minorista queda solo lo que se cobra. */}
              {!isAgent ? (
                <InfoItem
                  label={t('travelServices.finalPrice')}
                  value={money(booking.finalAmountMinor)}
                />
              ) : (
                <>
                  <InfoItem
                    label={t('travelServices.detail.net')}
                    value={money(booking.netAmountMinor)}
                  />
                  <InfoItem
                    label={t('travelServices.detail.markup')}
                    value={
                      booking.markupValue != null
                        ? booking.markupType === 'percentage'
                          ? `${booking.markupValue}%`
                          : money(Math.round(booking.markupValue * 100))
                        : booking.pricingModel === 'commissionable'
                          ? t('travelServices.grossPrice')
                          : '—'
                    }
                  />
                  {booking.pricingModel === 'commissionable' ? (
                    <InfoItem
                      label={t('travelServices.detail.commission')}
                      value={money(
                        booking.commissionAmountMinor,
                        booking.commissionCurrency ?? booking.currency,
                      )}
                    />
                  ) : (
                    <InfoItem
                      label={t('travelServices.finalPrice')}
                      value={money(booking.finalAmountMinor)}
                    />
                  )}
                </>
              )}
            </Grid>
            <CarBookingPaymentBreakdown booking={booking} isAgent={isAgent} />
          </Box>

          {booking.passengers && booking.passengers.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                {t('travelServices.booking.passengers')}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('travelServices.booking.firstName')}</TableCell>
                    <TableCell>{t('travelServices.booking.lastName')}</TableCell>
                    <TableCell>{t('travelServices.booking.passport')}</TableCell>
                    <TableCell>{t('travelServices.booking.flightsSection')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {booking.passengers.map((pax, index) => (
                    <TableRow key={index}>
                      <TableCell>{pax.firstName}</TableCell>
                      <TableCell>{pax.lastName}</TableCell>
                      <TableCell>{pax.passportNumber ?? '—'}</TableCell>
                      <TableCell>
                        {[pax.flightNumber, pax.returnFlightNumber]
                          .filter(Boolean)
                          .join(' / ') || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          <Box>
            {/* Sin la capacidad no hay política que pedir: el botón no existe.
                Este botón es informativo y se oculta también mientras no
                sabemos; el diálogo de cancelar, en cambio, pregunta ante la
                duda — decir "no la publica" sin haber preguntado sería
                afirmar algo que no sabemos justo cuando se decide la plata. */}
            {areConnectorCapabilitiesActionable &&
              cancellationPolicyVerdict !== 'unsupported' && (
              <Button
                size="small"
                onClick={() => void cancellation.enquirePolicy()}
                disabled={!canOperate || cancellation.isLoadingPolicy}
              >
                {t('travelServices.detail.viewPolicy')}
              </Button>
            )}
            {confirmedOptionalItemsFallbackVisible({
              confirmed: booking.confirmedOptionalItems,
            }) && <ConfirmedOptionalItems booking={booking} />}
            <BookingExtras
              bookingId={booking.id}
              verdict={extrasVerdict}
              moment={bookingMomentOf(booking.state)}
              hasRemoteBooking={canOperate}
              passengers={booking.passengers}
            />
            {booking.refund && (
              <Alert severity={booking.refund.state === 'error' ? 'warning' : 'info'} sx={{ mt: 1 }}>
                {booking.refund.state === 'pending'
                  ? t('travelServices.booking.refundPending')
                  : t(`travelServices.booking.refund${
                    booking.refund.state === 'error' ? 'Error' : 'Done'
                  }`, {
                    amount: money(booking.refund.amount.amountMinor, booking.refund.amount.currency),
                  })}
              </Alert>
            )}
            {/* La MISMA política que se lee al cancelar: por dónde entró el
                asesor no puede cambiar lo que la cancelación cuesta. Antes de
                pedirla no se dibuja nada — el botón de arriba es el pedido. */}
            {cancellation.asked && (
              <Box mt={1}>
                <CancellationPolicyPanel view={policyView} notice={cancellation.notice} />
              </Box>
            )}
          </Box>

          {/* El documento de la agencia va con los del viaje: decide solo si
              tiene superficie (reserva confirmada que vive en el hub). */}
          <VoucherAction booking={booking} />
          {ticketsQuery.data && (
            <BookingTickets
              tickets={ticketsQuery.data}
              vertical={booking.vertical}
              isRefreshing={ticketsQuery.isLoading}
              isPolling={ticketsQuery.isPolling}
              pollingExhausted={ticketsQuery.pollingExhausted}
              fetchVerdict={ticketsVerdict}
              onRefresh={ticketsQuery.request}
            />
          )}
          {ticketsQuery.isError && (
            <Alert severity="info">
              {apiErrorMessage(ticketsQuery.error) ??
                t('travelServices.booking.ticketsNotReady')}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('travelServices.booking.close')}
        </Button>
        {/* Un solo botón de sincronizar por vez: con la tarjeta del estado
            ofreciéndolo —y explicando por qué— el pie no lo repite. */}
        {canOperate &&
          syncCapabilityAvailable &&
          recoverySurface?.spec.action !== 'sync' &&
          booking.activeOperationStatus !== 'in_progress' && (
          <LoadingButton loading={sync.isLoading} onClick={syncBooking}>
            {t('travelServices.booking.sync')}
          </LoadingButton>
        )}
        {canOperate && cancelCapabilityAvailable && canCancelBooking(booking) && (
          <Tooltip
            title={
              booking.state === 'held'
                ? t('travelServices.booking.cancelHoldTooltip')
                : ''
            }
          >
            <span>
              <LoadingButton
                color="error"
                loading={cancellation.isCancelling}
                onClick={cancellation.ask}
              >
                {booking.state === 'held'
                  ? t('travelServices.booking.cancelHold')
                  : t('travelServices.detail.cancelBooking')}
              </LoadingButton>
            </span>
          </Tooltip>
        )}
        {canOperate && confirmCapabilityAvailable && canConfirmBooking(booking) && (
          <>
            {/* Nunca un botón gris mudo: el motivo va pegado al botón y sale
                de la MISMA lista que lo deshabilita. */}
            {confirmBlockedByQuestions && (
              <Typography
                id={CONFIRM_BLOCKED_ID}
                variant="caption"
                color="text.secondary"
                sx={{ maxWidth: 320 }}
              >
                {t('travelServices.booking.blockedForConfirm', {
                  reasons: [
                    unansweredQuestions.length > 0
                      ? t('travelServices.booking.blocked.questions')
                      : '',
                    mandatoryAnswersOverflow
                      ? t('travelServices.booking.blocked.answersOverflow')
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' · '),
                })}
              </Typography>
            )}
            <Tooltip title={t('travelServices.booking.confirmTooltip')}>
              <span>
                <LoadingButton
                  variant="contained"
                  loading={confirm.isLoading}
                  disabled={confirmBlockedByQuestions}
                  aria-describedby={
                    confirmBlockedByQuestions ? CONFIRM_BLOCKED_ID : undefined
                  }
                  onClick={() =>
                    run(
                      async () => {
                        const updated = await confirm.mutateAsync({
                          bookingId: booking.id,
                          // Solo lo respondido, con el `code` de la pregunta:
                          // nada inventado para las que quedaron vacías.
                          answers: questionAnswerPayload(pendingQuestions, answers),
                        });
                        clearQuestionAnswersDraft(myProfile?.id, booking.id);
                        return updated;
                      },
                      'travelServices.booking.confirmError',
                      booking.id,
                    )
                  }
                >
                  {t('travelServices.booking.confirm')}
                </LoadingButton>
              </span>
            </Tooltip>
          </>
        )}
        {booking.state === 'confirmed' && ticketSurfaces.canFetch && (
          <LoadingButton
            variant="outlined"
            loading={ticketsQuery.isLoading}
            onClick={ticketsQuery.request}
          >
            {t('travelServices.booking.getTickets')}
          </LoadingButton>
        )}
        {/* Un botón que desaparece sin motivo se lee como bug: cuando el
            proveedor DICE que no emite tickets, se dice. */}
        {booking.state === 'confirmed' && ticketSurfaces.showUnsupportedHint && (
          <Typography variant="caption" color="text.secondary">
            {t('travelServices.booking.ticketsNotSupported')}
          </Typography>
        )}
      </DialogActions>

      <CancelBookingDialog
        open={cancellation.isOpen}
        view={policyView}
        notice={cancellation.notice}
        isCancelling={cancellation.isCancelling}
        refusal={cancellation.refusal}
        onClose={cancellation.close}
        onConfirm={() => void cancellation.confirm()}
      />
    </Dialog>
  );
}
